import { vehicleCalculation, energyCalculation, unavoidableCalculation, flightCalculation, wasteCalculation, dietCalculation } from './Calculations.js';
import _ from 'lodash';
import getDataByCountryCode from "../common/impact/data/CountryData";
import {currencies} from "../../util/priceFormatter" ;

const drivingSubLabels = ['Minimal', 'Lots' ]
const energySubLabels = ['Minimal' ,  'Lots']
const dietSubLabels = ['Plant Based' , 'Meat Lover' ]
const wasteSubLabels = ['Little', 'Lots' ]

  const questionsLocalizationConfig = [
    {
      code: 'WASTE',
      label: 'Typically, how full is your bin at the end of the week?',
      locales: {
        US: 'Typically, how full is your trash can at the end of the week?',
      },
      tooltip: {
        label: `If you don't have a personal bin, estimate your weekly waste by comparing it to a standard bin size or provide an approximation, like 'equivalent to half a standard bin.`,
        locales: {
          US: `If you don't have a personal trash can, estimate your weekly waste by comparing it to a standard can size or provide an approximation, like 'equivalent to half a standard can.`,
        },
      }
    },
    {
      code: 'ALMOST_EMPTY',
      label: 'Bin almost empty, minimal waste',
      locales: {
        US: 'Can almost empty, minimal waste',
      },
    },
    {
      code: 'LESS_THAN_HALF',
      label: 'Bin less than half full',
      locales: {
        US: 'Can less than half full',
      },
    },
    {
      code: 'HALF_FULL',
      label: 'Bin half full',
      locales: {
        US: 'Can half full',
      },
    },
    {
      code: 'MORE_THAN_HALF',
      label: 'Bin more than half full',
      locales: {
        US: 'Can more than half full',
      },
    },
    {
      code: 'FULL',
      label: 'Bin full',
      locales: {
        US: 'Can full',
      },
    },
  ];

  let QuestionsConfig = class QuestionsConfig {

    constructor(countryCode){
      this.countryCode = countryCode.toUpperCase();

      const customisedConfig = _.cloneDeep(this.baseQuestions);

      let countryOveride = this.configOverides[this.countryCode];
      
      //If we don't have country data then use EU data; if not EU country then flag as unsupported
      if(!countryOveride){
        const countryData = getDataByCountryCode(countryCode);
        if(! countryData.eu){
          this.isUnsupportedCountry = true;
        }
        countryOveride = this.configOverides['EU'];
      }

      _.merge(customisedConfig,countryOveride)

      this.questions = customisedConfig;
    }
  
  getDrivingDescription(code) {
    const result = questionsLocalizationConfig.find(q => q.code === code).locales[this.countryCode];
    if (result) {
      return result;
    } else {
      return questionsLocalizationConfig.find(q => q.code === code).label;
    }
  }

  getTooltip(code) {
    const result = questionsLocalizationConfig?.find(q => q.code === code)?.tooltip.locales[this.countryCode];
    if (result) {
      return result;
    } else {
      return questionsLocalizationConfig.find(q => q.code === code)?.tooltip.label;
    }
  }

   getDrivingLabel  (value, questionConfig) {
    if(value<520){
      return 'Very little';
    }
    else{
      // const roundedYearlyValue=Math.round(value/100)*100;
      const monthlyValue=Math.round(value/52);
      return (`${monthlyValue}${questionConfig.unit} weekly / ${value}${questionConfig.unit} yearly`)
    }
  }
  
   getEnergyLabel  (value, questionConfig) {
    if(value<10){
      return 'Very little';
    }
    else{
      const currencyDisplay = currencies[questionConfig.unit] ? currencies[questionConfig.unit] : questionConfig.unit;
      return (`Around ${currencyDisplay}${value} monthly`)
    }
  }
  
   getWasteLabel(value, questionConfig)  {
    const numLabels=5;

    const gap=(questionConfig.max-questionConfig.min)/(numLabels-1);

    let testAmount = questionConfig.min-gap+0.1;
    if(value<=(testAmount+=gap)) return this.getDrivingDescription('ALMOST_EMPTY');
    if(value<=(testAmount+=gap)) return this.getDrivingDescription('LESS_THAN_HALF');
    if(value<=(testAmount+=gap)) return this.getDrivingDescription('HALF_FULL');
    if(value<=(testAmount+=gap)) return this.getDrivingDescription('MORE_THAN_HALF');
    if(value<=(testAmount+=gap)) return this.getDrivingDescription('FULL');
  
  }
 
  //constants and calc to help work out our step size (NZ diet min is 1.4t and max us 3.5t)

   getDietLabel (value,questionConfig) {
      const numLabels=6;

      const gap=(questionConfig.max-questionConfig.min)/(numLabels-1);

      let testAmount = questionConfig.min-gap+0.1;
      if(value<=(testAmount+=gap)) return'100% Plant Based';
      if(value<=(testAmount+=gap)) return 'Vegetarian or very low meat';
      if(value<=(testAmount+=gap)) return 'Meat 2-3 times weekly';
      if(value<=(testAmount+=gap)) return 'Meat 4-5 times weekly';
      if(value<=(testAmount+=gap)) return 'Meat 6+ times weekly';
      if(value<=(testAmount+=gap)) return 'Meat every day, lots of red meat';
  }

   baseQuestions = {
    adults: { rootAlias: 'HOUSEHOLD', alias: 'adults', name: 'Adults', min: 1, max: 9 },
    children: { rootAlias: 'HOUSEHOLD', alias: 'children', name: 'Children', description: '2 to 13 years old', min: 0, max: 9 },
    unavoidableCountryEmissions: { rootAlias: 'UNAVOIDABLE', alias: 'unavoidableCountryEmissions', multiplier: 3.5, calcFunction: unavoidableCalculation  },
    driving: { rootAlias: 'DRIVING', isArray: true, name: 'DRIVING', description: 'What type of car are you drive, and how far do you drive in a typical week?', valueLabelDisplay: 'off', subLabels: drivingSubLabels, getLabel: this.getDrivingLabel.bind(this), unit:'km', calcFunction: vehicleCalculation},
    energy: { rootAlias: 'ENERGY', name: 'ENERGY', tooltipContent: 'Some energy providers offer carbon neutral energy which they have already offset. You will find this information on your utility bill.', description: 'Your share of household electricity, gas and other fuels', valueLabelDisplay: 'off', subLabels: energySubLabels, getLabel:this.getEnergyLabel.bind(this), calcFunction: energyCalculation},
    diet: { rootAlias: 'DIET', name: 'Diet', description: 'Average composition within a week',multiplier: 1, min: 1.5, max: 3.3, step: 0.05, defaultValue:2.5, valueLabelDisplay: 'off', subLabels: dietSubLabels, getLabel:this.getDietLabel.bind(this), calcFunction: dietCalculation },
    waste: { rootAlias: 'WASTE', name: 'WASTE', getTooltip: this.getTooltip.bind(this), getDescription: this.getDrivingDescription.bind(this), min: 1, max: 6, step: 10, valueLabelDisplay: 'off', subLabels: wasteSubLabels, getLabel:this.getWasteLabel.bind(this), calcFunction: wasteCalculation },
    quickFlights: { rootAlias: 'FLIGHTS', alias: 'numQuick', name: 'Quick', description: '1-2 hours each way', multiplier: 0.3, min: 0, max: 30, calcFunction:flightCalculation },
    shortFlights: { rootAlias: 'FLIGHTS', alias: 'numShort', name: 'Short', description: '3-5 hours each way', multiplier: 0.8, min: 0, max: 30, calcFunction:flightCalculation },
    intermediateFlights: { rootAlias: 'FLIGHTS', alias: 'numIntermediate', name: 'Intermediate', description: '6-9 hours each way', multiplier: 1.4, min: 0, max: 30, calcFunction:flightCalculation },
    longFlights: { rootAlias: 'FLIGHTS', alias: 'numLong', name: 'Long', description: '10+ hours each way', multiplier: 2.0, min: 0, max: 30, calcFunction:flightCalculation }
  };

// Some data updates from Kat's spreadsheet. Also see old commit messages for other data source(s)
// https://docs.google.com/spreadsheets/d/1Yrfu0pYojayoG_hgHEavJVumf_9_blQA8v6XQMYPhP0/edit#gid=0
   configOverides = {
      NZ: {
        unavoidableCountryEmissions: {multiplier: 3},
        driving: { multiplier: 0.000236, min: 0, max: 60000, step: 100, defaultValue:10000}, //multiplier calculated based on drivingData.js  Medium Petrol car
        energy: {multiplier: 0.003912, min: 0, max: 600, step: 10, defaultValue:200, unit:'NZD'},
        waste: {multiplier: 0.000242, min:200, max:1800, defaultValue:700}
      },
      US: {
        unavoidableCountryEmissions: {multiplier: 3.1},
        driving: { multiplier: 0.00039, min: 0, max: 40000, step: 100, defaultValue:13000, unit:'mi'},
        energy: {multiplier: 0.04536, min: 0, max: 300, step: 10, defaultValue:130, unit:'USD'},
        waste: {multiplier: 0.000424, min:200, max:1800, defaultValue:800}
      },
      EU: {
        unavoidableCountryEmissions: {multiplier: 2.5},
        diet: { min: 1, max: 3, defaultValue:1.5},
        driving: { multiplier: 0.00012, min: 0, max: 60000, step: 100, defaultValue:12000},
        energy: {multiplier: 0.03576, min: 0, max: 200, step: 10, defaultValue:65, unit:'EUR'},
        waste: {multiplier: 0.000231, min:100, max:1300, defaultValue:500}
      },
      AU: {
        unavoidableCountryEmissions: {multiplier: 13}, //tonnesCo2
        diet: { min: 1.5, max: 3.7, defaultValue:2.5}, //tonnesCo2
        driving: { multiplier: 0.00039, min: 0, max: 60000, step: 100, defaultValue:18000}, //km's driven per year, multiplier is tonnesCo2/km
        energy: {multiplier: 0.015 , min: 0, max: 600, step: 10, defaultValue:200, unit:'AUD'}, //$spent per month, multiplier is tonnesCo2/$
        waste: {multiplier: 0.000424, min:200, max:1800, defaultValue:800} , //kg's generated per year, multiplier is tonnesCo2/kg waste
      },
      GB: {
        unavoidableCountryEmissions: {multiplier: 1.5}, //tonnesCo2
        diet: { min: 1, max: 3, defaultValue:1.5}, //tonnesCo2
        driving: { multiplier: 0.00039, min: 0, max: 40000, step: 100, defaultValue:3500, unit:'mi'}, //km's driven per year, multiplier is tonnesCo2/km
        energy: {multiplier: 0.022 , min: 0, max: 160, step: 10, defaultValue:110, unit:'GBP'}, //$spent per month, multiplier is tonnesCo2/$
        waste: {multiplier: 0.000446, min:300, max:850, defaultValue:550} , //kg's generated per year, multiplier is tonnesCo2/kg waste
      },
      IN: {
          unavoidableCountryEmissions: {multiplier: 0.7}, //tonnesCo2
          diet: { min: 0.2, max: 1.5, defaultValue:0.4}, //tonnesCo2
          //MULTIPLIER FROM NZ CONFIG!
          driving: { multiplier: 0.000236, min: 0, max: 30000, step: 100, defaultValue:3000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
          energy: {multiplier: 0.001652 , min: 0, max: 1500, step: 10, defaultValue:400, unit:'INR'}, //$spent per month, multiplier is tonnesCo2/$
          waste: {multiplier: 0.000446, min:180, max:350, defaultValue:200} , //kg's generated per year, multiplier is tonnesCo2/kg waste
      },
       JP: {
           unavoidableCountryEmissions: {multiplier: 3.2}, //tonnesCo2
           diet: { min: 0.6, max: 2.0, defaultValue:1.3}, //tonnesCo2
           //MULTIPLIER FROM NZ CONFIG!
           driving: { multiplier: 0.000236, min: 0, max: 30000, step: 100, defaultValue:5000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
           energy: {multiplier: 0.000186  , min: 0, max: 20000, step: 10, defaultValue:10000, unit:'JPY'}, //$spent per month, multiplier is tonnesCo2/$
           waste: {multiplier: 0.000231, min:200, max:500, defaultValue:350} , //kg's generated per year, multiplier is tonnesCo2/kg waste
       },
       RO: {
           unavoidableCountryEmissions: {multiplier: 1.5}, //tonnesCo2
           diet: { min: 1.0, max: 3.0, defaultValue:1.5}, //tonnesCo2
           //MULTIPLIER FROM NZ CONFIG!
           driving: { multiplier: 0.000236, min: 0, max: 40000, step: 100, defaultValue:10000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
           energy: {multiplier: 0.00382 , min: 0, max: 1000, step: 10, defaultValue:300, unit:'RON'}, //$spent per month, multiplier is tonnesCo2/$
           waste: {multiplier: 0.000231, min:100, max:1200, defaultValue:500} , //kg's generated per year, multiplier is tonnesCo2/kg waste
       },
       AE: {
           unavoidableCountryEmissions: {multiplier: 12}, //tonnesCo2
           diet: { min: 1.0, max: 6, defaultValue:3}, //tonnesCo2
           //MULTIPLIER FROM NZ CONFIG!
           driving: { multiplier: 0.00039, min: 0, max: 50000, step: 100, defaultValue:15000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
           energy: {multiplier: 0.001626 , min: 0, max: 1000, step: 10, defaultValue:750, unit:'AED'}, //$spent per month, multiplier is tonnesCo2/$
           waste: {multiplier: 0.000446, min:300, max:1500, defaultValue:650} , //kg's generated per year, multiplier is tonnesCo2/kg waste
       },
       SA: {
           unavoidableCountryEmissions: {multiplier: 10}, //tonnesCo2
           diet: { min: 1.0, max: 6, defaultValue:3}, //tonnesCo2
           //MULTIPLIER FROM NZ CONFIG!
           driving: { multiplier: 0.00039, min: 0, max: 50000, step: 100, defaultValue:15000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
           energy: {multiplier: 0.00316 , min: 0, max: 2500, step: 10, defaultValue:1850, unit:'SR'}, //$spent per month, multiplier is tonnesCo2/$
           waste: {multiplier: 0.000446, min:400, max:1500, defaultValue:620} , //kg's generated per year, multiplier is tonnesCo2/kg waste
       },
       TH: {
           unavoidableCountryEmissions: {multiplier: 1.9}, //tonnesCo2
           diet: { min: 0.6, max: 2, defaultValue:1.3}, //tonnesCo2
           //MULTIPLIER FROM NZ CONFIG!
           driving: { multiplier: 0.000236, min: 0, max: 30000, step: 100, defaultValue:3000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
           energy: {multiplier: 0.00107 , min: 0, max: 7000, step: 10, defaultValue:1550, unit:'THB'}, //$spent per month, multiplier is tonnesCo2/$
           waste: {multiplier: 0.000446, min:180, max:350, defaultValue:200} , //kg's generated per year, multiplier is tonnesCo2/kg waste
       },
       AZ: {
           unavoidableCountryEmissions: {multiplier: 1.5}, //tonnesCo2
           diet: { min: 1.0, max: 3, defaultValue:1.5}, //tonnesCo2
           //MULTIPLIER FROM NZ CONFIG!
           driving: { multiplier: 0.000236, min: 0, max: 40000, step: 100, defaultValue: 10000, unit:'km'}, //km's driven per year, multiplier is tonnesCo2/km
           energy: {multiplier: 0.022 , min: 0, max: 180, step: 10, defaultValue:118, unit:'AZN'}, //$spent per month, multiplier is tonnesCo2/$
           waste: {multiplier: 0.000231, min:150, max:500, defaultValue:350} , //kg's generated per year, multiplier is tonnesCo2/kg waste
       }
  };
}
export default QuestionsConfig
